import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { descargarInventarioExcel, getProductos, searchProductos, deleteProducto } from "../services/productos";
import SearchBar from "../components/SearchBar";
import DeleteWarning from "../components/DeleteWarning";
import Pagination from "../components/Pagination";
import '../styles/Button.css';
import { FaFileExcel } from "react-icons/fa";
import '../styles/ProductPage.css';
import { Producto, PaginationInfo } from "../types";

const Productos: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [productos, setProductos] = useState<Producto[]>([]);
  const [modalData, setModalData] = useState<any>(null);
  const [modalType, setModalType] = useState<string | null>(null);
  const [alertMessage, setAlertMessage] = useState<string>('');
  const [reFetch, setReFetch] = useState<number>(0);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pagination, setPagination] = useState<Partial<PaginationInfo>>({});

  // Extract query from URL
  const queryParams = new URLSearchParams(location.search);
  const currentQuery = queryParams.get('q') || '';
  const isSearching = !!currentQuery.trim();

  useEffect(() => {
    const fetchData = async () => {
      if (isSearching) {
        const searchResults: any = await searchProductos(currentQuery, currentPage, 10);
        const matches = searchResults.productos || searchResults.data || [];
        if (matches.length > 0) {
          setProductos(matches);
          setPagination(searchResults.pagination || {});
        } else {
          setProductos([]);
          setPagination({});
        }
      } else {
        const data = await getProductos(currentPage, 10);
        setProductos(data.productos || []);
        setPagination(data.pagination || {});
      }
    };
    fetchData();
  }, [reFetch, currentPage, location.search]);

  const handleSearch = (query: string) => {
    setCurrentPage(1);
    // Just navigate, logic above handles fetch
    if (query.trim() !== '') {
      navigate(`?q=${encodeURIComponent(query)}`, { replace: true });
    } else {
      navigate(location.pathname, { replace: true });
    }
  };

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleClose = () => {
    setModalType(null);
    setModalData(null);
  };

  // Editar elemento
  // Editar elemento (Triggered via context/other means?)
  // Actually, standard edit flow is navigate
  const handleEdit = (item: Producto) => {
    navigate(`/productos/editar/${item.id}`);
  };

  const handleDelete = async () => {
    if (modalData) {
      // deleteProducto takes only ID
      await deleteProducto(modalData.item.id);
      handleAlert(`¡Producto eliminado correctamente!`);
    }
  };

  const handleAlert = (message: string) => {
    setAlertMessage(message);
    setReFetch(prev => prev + 1);
    setTimeout(() => setAlertMessage(''), 5000);
  };

  return (
    <div className="page">
      <div className="productos-header">
        <h2>Productos</h2>
        <button
          className="btn-excel"
          onClick={descargarInventarioExcel}
        >
          <FaFileExcel style={{ marginRight: '8px', fontSize: "1.2em" }} />
          Descargar Inventario en Excel
        </button>
      </div>
      <SearchBar
        placeholder="Buscar productos..."
        onSearch={handleSearch}
      />
      {alertMessage && (
        <div className="alert alert-success">{alertMessage}</div>
      )}
      {isSearching && (
        <div className="search-results-banner">
          <span className="search-results-text">
            Resultados para: &quot;{currentQuery}&quot;
            {pagination.totalItems !== undefined && ` (${pagination.totalItems} encontrados)`}
          </span>
          <button
            className="clear-search-btn"
            onClick={() => navigate(location.pathname)}
          >
            Limpiar búsqueda
          </button>
        </div>
      )}
      <table className="table">
        <thead>
          <tr className="table-header">
            <th>Id</th>
            <th>Nombre</th>
            <th>Marca</th>
            <th>Modelo</th>
            <th>Referencia</th>
            <th>Precio</th>
            <th>Stock</th>
          </tr>
        </thead>
        <tbody>
          {productos?.length > 0 ? (
            productos.map((producto) => (
              <tr
                key={producto.id}
                className="table-row"
                onClick={() => {
                  navigate(`/productos/${producto.id}`, { state: { product: producto } });
                }}
                style={{ cursor: 'pointer' }}
              >
                <td>{producto.id}</td>
                <td>{producto.nombre}</td>
                <td>{producto.marca}</td>
                <td>{producto.modelo}</td>
                <td>{producto.referencia}</td>
                <td>${producto.precio_venta}</td>
                <td>{(producto as any).Inventario?.stock || 0}</td>
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan={7} style={{ textAlign: 'center', padding: '20px', color: '#666' }}>
                {isSearching ? 'No se encontraron productos' : 'No hay productos disponibles'}
              </td>
            </tr>
          )}
        </tbody>
      </table>

      <Pagination
        currentPage={pagination.currentPage || 1}
        totalPages={pagination.totalPages || 1}
        hasNextPage={currentPage < (pagination.totalPages || 1)}
        hasPrevPage={currentPage > 1}
        onPageChange={handlePageChange}
      />

      {modalType && (
        <div className="modal">
          <div className="modal-content">
            {modalType === 'Eliminar' && (
              <DeleteWarning
                itemType="Producto"
                itemName={modalData?.item?.nombre}
                onCancel={handleClose}
                onConfirm={handleDelete}
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Productos;